// ============================
// json2shp.ts
// GeoJSON → Shapefile ZIP Export
// Requires: npm install shp-write-update jszip
// ============================

import shpwrite from 'shp-write-update';
import { FeatureCollection, Feature, Geometry } from 'geojson';

interface ExportOptions {
  exEPSG?: string;
  filename?: string;
  folder?: string;
}

export class GeoJSONExporter {
  // ============================
  // Main Export Function
  // ============================
  async exportGeoJSON(input: FeatureCollection<Geometry> | Geometry[], options?: ExportOptions): Promise<void> {
    const { exEPSG = 'EPSG:4326', filename = 'mrpro-gis-export.zip', folder = '' } = options || {};

    // 1. Normalize input → Feature[]
    const rawFeatures = this.toFeatures(input);

    // 2. Pipeline
    const fc: FeatureCollection = {
      type: 'FeatureCollection',
      features: this.normalizeFeatureProperties(this.addUniqueId(this.sanitizeFeatures(rawFeatures))),
    };

    console.log('Export feature count:', fc.features.length);
    console.log('Geometry types:', [...new Set(fc.features.map(f => f.geometry?.type))]);
    console.log('Property keys:', Object.keys(fc.features[0]?.properties || {}));

    // 3. Export via shp-write-update
    const zipBuffer = (await shpwrite.zip(fc, {
      outputType: 'arraybuffer',
      compression: 'DEFLATE',
      prj: this.getPrjByEPSG(exEPSG),
      folder: folder || undefined,
      types: {
        point: 'points',
        line: 'polylines',
        polygon: 'polygons',
        multipolygon: 'multipolygons',
        multiline: 'multilines',
      },
    })) as ArrayBuffer;

    this.downloadBlob(new Blob([zipBuffer], { type: 'application/zip' }), filename);
  }

  // ============================
  // Step 0: Normalize Input
  // Accepts raw Geometry[] or FeatureCollection
  // ============================
  private toFeatures(input: FeatureCollection<Geometry> | Geometry[]): Feature<Geometry>[] {
    if (Array.isArray(input)) {
      // Raw geometry array → wrap each as Feature
      return input.map(geom => ({
        type: 'Feature' as const,
        geometry: geom,
        properties: {},
      }));
    }

    // FeatureCollection → ensure every item is a proper Feature
    return input.features.map(f => {
      if (f.type !== 'Feature') {
        return {
          type: 'Feature' as const,
          geometry: f as unknown as Geometry,
          properties: {},
        };
      }
      return f;
    });
  }

  // ============================
  // Step 1: Sanitize Geometries
  // Remove null / invalid geometries
  // ============================
  private sanitizeFeatures(features: Feature<Geometry>[]): Feature<Geometry>[] {
    return features.filter(f => {
      if (!f.geometry) return false;

      switch (f.geometry.type) {
        case 'Point':
          return (
            Array.isArray(f.geometry.coordinates) &&
            f.geometry.coordinates.length >= 2 &&
            isFinite(f.geometry.coordinates[0]) &&
            isFinite(f.geometry.coordinates[1])
          );

        case 'LineString':
          return (
            Array.isArray(f.geometry.coordinates) &&
            f.geometry.coordinates.length >= 2 &&
            f.geometry.coordinates.every(c => Array.isArray(c) && c.length >= 2 && isFinite(c[0]) && isFinite(c[1]))
          );

        case 'MultiLineString':
          return (
            Array.isArray(f.geometry.coordinates) &&
            f.geometry.coordinates.length > 0 &&
            f.geometry.coordinates.every(line => Array.isArray(line) && line.length >= 2)
          );

        case 'Polygon':
          return (
            Array.isArray(f.geometry.coordinates) &&
            f.geometry.coordinates.length > 0 &&
            f.geometry.coordinates[0].length >= 4 &&
            f.geometry.coordinates[0].every(c => Array.isArray(c) && isFinite(c[0]) && isFinite(c[1]))
          );

        case 'MultiPolygon':
          return Array.isArray(f.geometry.coordinates) && f.geometry.coordinates.length > 0;

        default:
          return true;
      }
    });
  }

  // ============================
  // Step 2: Add Unique ID
  // id is always the first property, 1-based integer
  // ============================
  private addUniqueId(features: Feature<Geometry>[]): Feature<Geometry>[] {
    return features.map((f, index) => ({
      ...f,
      properties: {
        id: index + 1, // ← first property, 1-based
        ...f.properties,
      },
    }));
  }

  // ============================
  // Step 3: Normalize Properties
  // Unify schema across all features — fixes DBF/SHP record mismatch
  // Every feature gets every key, missing → null
  // ============================
  private normalizeFeatureProperties(features: Feature<Geometry>[]): Feature<Geometry>[] {
    // Collect all unique keys across all features
    const allKeys = Array.from(new Set(features.flatMap(f => (f.properties ? Object.keys(f.properties) : []))));

    // id always first, rest after
    const orderedKeys = ['id', ...allKeys.filter(k => k !== 'id')];

    return features.map(f => ({
      ...f,
      properties: Object.fromEntries(orderedKeys.map(key => [key, this.sanitizeValue(f.properties?.[key])])),
    }));
  }

  // ============================
  // Sanitize a single property value
  // ============================
  private sanitizeValue(val: any): any {
    if (val === undefined || val === null) return null;
    if (val instanceof Date) return val.toISOString();
    if (typeof val === 'object') return JSON.stringify(val); // nested objects → string
    if (typeof val === 'number' && !isFinite(val)) return null; // NaN / Infinity → null
    return val;
  }

  // ============================
  // PRJ by EPSG
  // ============================
  private getPrjByEPSG(epsg: string): string {
    switch (epsg) {
      //WGS 84 World
      case 'EPSG:4326':
        return `GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137,298.257223563]],PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]]`;
      //Swiss CH1903 + / LV95
      case 'EPSG:2056':
        return `GEOGCS["CH1903+", DATUM["CH1903+", SPHEROID["Bessel 1841",6377397.155,299.1528128], TOWGS84[674.374,15.056,405.346,0,0,0,0]], PRIMEM["Greenwich",0], UNIT["degree",0.0174532925199433]]`;
      //Swiss CH1903 / LV03
      case 'EPSG:21781':
        return `GEOGCS["CH1903", DATUM["CH1903", SPHEROID["Bessel 1841",6377397.155,299.1528128]], PRIMEM["Greenwich",0], UNIT["degree",0.0174532925199433]]`;
      //ETRS89 / UTM zone 31N
      case 'EPSG:25831':
        return `GEOGCS["ETRS89", DATUM["European_Terrestrial_Reference_System_1989", SPHEROID["GRS 1980",6378137,298.257222101, AUTHORITY["EPSG","7019"]], TOWGS84[0,0,0,0,0,0,0], AUTHORITY["EPSG","6258"]], PRIMEM["Greenwich",0, AUTHORITY["EPSG","8901"]], UNIT["degree",0.0174532925199433, AUTHORITY["EPSG","9122"]], AUTHORITY["EPSG","4258"]], PROJECTION["Transverse_Mercator"], PARAMETER["latitude_of_origin",0], PARAMETER["central_meridian",3], PARAMETER["scale_factor",0.9996], PARAMETER["false_easting",500000], PARAMETER["false_northing",0], UNIT["metre",1, AUTHORITY["EPSG","9001"]], AXIS["Easting",EAST], AXIS["Northing",NORTH], AUTHORITY["EPSG","25831"]`;
      //ETRS89 / UTM zone 32N
      case 'EPSG:25832':
        return `GEOGCS["ETRS89", DATUM["European_Terrestrial_Reference_System_1989", SPHEROID["GRS 1980",6378137,298.257222101, AUTHORITY["EPSG","7019"]], TOWGS84[0,0,0,0,0,0,0], AUTHORITY["EPSG","6258"]], PRIMEM["Greenwich",0, AUTHORITY["EPSG","8901"]], UNIT["degree",0.0174532925199433, AUTHORITY["EPSG","9122"]], AUTHORITY["EPSG","4258"]], PROJECTION["Transverse_Mercator"], PARAMETER["latitude_of_origin",0], PARAMETER["central_meridian",9], PARAMETER["scale_factor",0.9996], PARAMETER["false_easting",500000], PARAMETER["false_northing",0], UNIT["metre",1, AUTHORITY["EPSG","9001"]], AXIS["Easting",EAST], AXIS["Northing",NORTH], AUTHORITY["EPSG","25832"]`;
      //ETRS89 / UTM zone 33N
      case 'EPSG:25833':
        return `GEOGCS["ETRS89", DATUM["European_Terrestrial_Reference_System_1989", SPHEROID["GRS 1980",6378137,298.257222101, AUTHORITY["EPSG","7019"]], TOWGS84[0,0,0,0,0,0,0], AUTHORITY["EPSG","6258"]], PRIMEM["Greenwich",0, AUTHORITY["EPSG","8901"]], UNIT["degree",0.0174532925199433, AUTHORITY["EPSG","9122"]], AUTHORITY["EPSG","4258"]], PROJECTION["Transverse_Mercator"], PARAMETER["latitude_of_origin",0], PARAMETER["central_meridian",15], PARAMETER["scale_factor",0.9996], PARAMETER["false_easting",500000], PARAMETER["false_northing",0], UNIT["metre",1, AUTHORITY["EPSG","9001"]], AXIS["Easting",EAST], AXIS["Northing",NORTH], AUTHORITY["EPSG","25833"]`;
      //Gauss-Kruger Zone 2 (Mittelmeridiane 6 Grad)
      case 'EPSG:31466':
        return `GEOGCS["DHDN", DATUM["Deutsches_Hauptdreiecksnetz", SPHEROID["Bessel 1841",6377397.155,299.1528128], EXTENSION["PROJ4_GRIDS","de_adv_BETA2007.tif"]], PRIMEM["Greenwich",0, AUTHORITY["EPSG","8901"]], UNIT["degree",0.0174532925199433, AUTHORITY["EPSG","9122"]], AUTHORITY["EPSG","4314"]], PROJECTION["Transverse_Mercator"], PARAMETER["latitude_of_origin",0], PARAMETER["central_meridian",6], PARAMETER["scale_factor",1], PARAMETER["false_easting",2500000], PARAMETER["false_northing",0], UNIT["metre",1, AUTHORITY["EPSG","9001"]], AUTHORITY["EPSG","31466"]`;
      //Gauss-Kruger Zone 3 (Mittelmeridiane 9 Grad)
      case 'EPSG:31467':
        return `GEOGCS["DHDN", DATUM["Deutsches_Hauptdreiecksnetz", SPHEROID["Bessel 1841",6377397.155,299.1528128], EXTENSION["PROJ4_GRIDS","de_adv_BETA2007.tif"]], PRIMEM["Greenwich",0, AUTHORITY["EPSG","8901"]], UNIT["degree",0.0174532925199433, AUTHORITY["EPSG","9122"]], AUTHORITY["EPSG","4314"]], PROJECTION["Transverse_Mercator"], PARAMETER["latitude_of_origin",0], PARAMETER["central_meridian",9], PARAMETER["scale_factor",1], PARAMETER["false_easting",3500000], PARAMETER["false_northing",0], UNIT["metre",1, AUTHORITY["EPSG","9001"]], AUTHORITY["EPSG","31467"]`;
      //Gauss-Kruger Zone 4 (Mittelmeridiane 12 Grad)
      case 'EPSG:31468':
        return `GEOGCS["DHDN", DATUM["Deutsches_Hauptdreiecksnetz", SPHEROID["Bessel 1841",6377397.155,299.1528128], EXTENSION["PROJ4_GRIDS","de_adv_BETA2007.tif"]], PRIMEM["Greenwich",0, AUTHORITY["EPSG","8901"]], UNIT["degree",0.0174532925199433, AUTHORITY["EPSG","9122"]], AUTHORITY["EPSG","4314"]], PROJECTION["Transverse_Mercator"], PARAMETER["latitude_of_origin",0], PARAMETER["central_meridian",12], PARAMETER["scale_factor",1], PARAMETER["false_easting",4500000], PARAMETER["false_northing",0], UNIT["metre",1, AUTHORITY["EPSG","9001"]], AUTHORITY["EPSG","31468"]`;
      //Gauss-Kruger Zone 5 (Mittelmeridiane 15 Grad)
      case 'EPSG:31469':
        return `GEOGCS["DHDN", DATUM["Deutsches_Hauptdreiecksnetz", SPHEROID["Bessel 1841",6377397.155,299.1528128], EXTENSION["PROJ4_GRIDS","de_adv_BETA2007.tif"]], PRIMEM["Greenwich",0, AUTHORITY["EPSG","8901"]], UNIT["degree",0.0174532925199433, AUTHORITY["EPSG","9122"]], AUTHORITY["EPSG","4314"]], PROJECTION["Transverse_Mercator"], PARAMETER["latitude_of_origin",0], PARAMETER["central_meridian",15], PARAMETER["scale_factor",1], PARAMETER["false_easting",5500000], PARAMETER["false_northing",0], UNIT["metre",1, AUTHORITY["EPSG","9001"]], AUTHORITY["EPSG","31469"]`;
      //Belge 1972 / Belgian Lambert 72
      case 'EPSG:31370':
        return `GEOGCS["BD72", DATUM["Reseau_National_Belge_1972", SPHEROID["International 1924",6378388,297], TOWGS84[-106.8686,52.2978,-103.7239,0.3366,-0.457,1.8422,-1.2747]], PRIMEM["Greenwich",0, AUTHORITY["EPSG","8901"]], UNIT["degree",0.0174532925199433, AUTHORITY["EPSG","9122"]], AUTHORITY["EPSG","4313"]], PROJECTION["Lambert_Conformal_Conic_2SP"], PARAMETER["latitude_of_origin",90], PARAMETER["central_meridian",4.36748666666667], PARAMETER["standard_parallel_1",51.1666672333333], PARAMETER["standard_parallel_2",49.8333339], PARAMETER["false_easting",150000.013], PARAMETER["false_northing",5400088.438], UNIT["metre",1, AUTHORITY["EPSG","9001"]], AXIS["Easting",EAST], AXIS["Northing",NORTH], AUTHORITY["EPSG","31370"]`;

      default:
        return `GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137,298.257223563]],PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]]`;
    }
  }

  // ============================
  // Download Helper
  // ============================
  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// ============================
// Usage
// ============================
// const exporter = new GeoJSONExporter();
//
// // From FeatureCollection:
// await exporter.exportGeoJSON(featureCollection, {
//   exEPSG: "EPSG:4326",
//   filename: "export.zip"
// });
//
// // From raw geometry array:
// await exporter.exportGeoJSON(geometriesArray, {
//   exEPSG: "EPSG:4326",
//   filename: "export.zip"
// });
